<template>
  <div
    :key="componentKey"
    class="dashboardIFrame"
  >
    <section
      id="powerbi-dashboard"
      v-if="renderPowerbiDashboard && forceRendering"
    />
    <iframe
      id="iframe"
      ref="iframe"
      v-show="renderDremioDashboard"
      :src="formatedURL"
      width="100%"
      height="100%"
      border="0"
      frameborder="0"
    />
  </div>
</template>

<script>
import { getCustomDashboardReportURLPowerbi } from '@/services/containers/partners/power-bi'
import { getLookerStudioToken } from '@/services/containers/partners/looker-studio'
import { mapGetters } from 'vuex'
import * as pbi from 'powerbi-client'
import { FEATURE_FLAGS } from '@/helpers/featureFlags'

export default {
  name: 'CustomDashboard',

  props: {},

  data: () => ({
    title: 'dashboard',
    formatedURL: null,
    iframe: null,
    componentKey: 0,
    powerbi: null,
    renderPowerbiDashboard: false,
    renderDremioDashboard: false,
    iframeId: '',
    forceRendering: true,
    isLoading: false,
    lastUserDashboards: null,
    lastPartnerId: null
  }),

  watch: {
    '$route': {
      handler(to, from) {
        const routeChanged =
          !from ||
          to.path !== from.path ||
          to.query.dashboard !== from.query.dashboard
        const isDashboardRoute = to.path.includes('/dashboard')

        if (!routeChanged || !isDashboardRoute) return

        this.reloadDashboard()
      },
      immediate: false
    },
    '$route.params': 'loadCustomDashboard',
    '$route.query.dashboard': function () {
      this.resetDashboardState()
      this.$showLoading()
      this.init()
    },
    'user.dashboards': {
      handler(newDashboards) {
        // reloads dashboard only when dashboards array changes (e.g.: partner switch)
        const dashboardsChanged = JSON.stringify(newDashboards) !== JSON.stringify(this.lastUserDashboards)
        if (!dashboardsChanged) return

        this.lastUserDashboards = newDashboards ? JSON.parse(JSON.stringify(newDashboards)) : null
        if (this.isLoading) return

        this.reloadDashboardWithLoading()
      },
      immediate: false
    },
    partner: {
      handler(newPartner, oldPartner) {
        // reloads dashboard when partner changes
        const partnerChanged = oldPartner && newPartner?._id !== oldPartner?._id
        if (!partnerChanged || this.isLoading) return

        this.lastPartnerId = newPartner?._id
        this.reloadDashboardWithLoading()
      },
      immediate: false
    }
  },

  created() {
    this.powerbi = new pbi.service.Service(
      pbi.factories.hpmFactory,
      pbi.factories.wpmpFactory,
      pbi.factories.routerFactory
    )
  },

  mounted() {
    this.lastUserDashboards = this.user.dashboards ? JSON.parse(JSON.stringify(this.user.dashboards)) : null
    this.lastPartnerId = this.partner?._id
    this.init()
  },

  computed: {
    ...mapGetters({
      user: 'getUser',
      partner: 'getPartner'
    }),
    isIndicatorsDashboardFlagActive() {
      return this.$isFeatureFlagActive(FEATURE_FLAGS.IndicatorsDashboard)
    }
  },

  methods: {
    reloadDashboard() {
      this.clearPowerBiEmbed()
      this.resetDashboardState()
      this.$showLoading()

      this.$nextTick(() => {
        this.init()
      })
    },

    reloadDashboardWithLoading() {
      this.isLoading = true
      this.clearPowerBiEmbed()
      this.resetDashboardState()

      this.$nextTick(() => {
        this.init()
        this.isLoading = false
      })
    },

    clearPowerBiEmbed() {
      if (!this.powerbi) return

      const element = document.getElementById('powerbi-dashboard')
      if (!element) return

      try {
        this.powerbi.reset(element)
      } catch (error) {
        // ignores error if there's no previous embed
      }
    },

    resetDashboardState() {
      this.componentKey += 1
      this.renderDremioDashboard = false
      this.forceRendering = false
      this.formatedURL = null
      this.iframeId = null
      if (this.$attrs?.iframe?.url) {
        this.$attrs.iframe.url = null
      }
    },

    init() {
      if (!this.$attrs?.iframe?.url && this.user.dashboards?.length) {
        const { query } = this.$route
        let dashboard = query?.dashboard
          ? this.user.dashboards.find((dash) => dash.name == query.dashboard)
          : this.user.dashboards[0]

        if (!dashboard) {
          dashboard = this.user.dashboards[0]
          if (dashboard) {
            this.syncRouteToDashboard(dashboard.name)
          }
        }

        if (!dashboard || !dashboard.url) return

        this.renderPowerbiDashboard =
          dashboard.url.includes('powerbi') && this.user.powerbiPermission
        if (
          (dashboard.url.includes('powerbi') && !this.user.powerbiPermission) ||
          (dashboard.url.includes('looker') && //Ajuste temporário, nuam próxima sprint trocará a propriedade tableauPermission para lookerPermission
            !this.user.tableauPermission)
        ) {
          const indicatorsContainerPath = this.isIndicatorsDashboardFlagActive ? 'loyalty' : 'partners'
          return this.$redirect(indicatorsContainerPath, 'indicators')
        }

        this.findDashboard({ url: dashboard.url, title: dashboard.name })
      }
    },

    findDashboard(iframe) {
      let dashboard = this.user.dashboards?.find((dash) => dash.name == iframe?.title)
      if (!dashboard && this.user.dashboards?.length) {
        this.syncRouteToDashboard(this.user.dashboards[0].name)
        return
      }
      if (!dashboard) {
        return
      }

      const target = { url: dashboard.url, title: dashboard.name }
      this.onLoadCallback(target)
      setTimeout(() => {
        this.loadIframe(target)
      }, 1000)
    },

    syncRouteToDashboard(dashboardName) {
      const path = `/partners/dashboard/${dashboardName}`
      const currentPath = this.$route.path
      const currentDashboard = this.$route.query?.dashboard
      if (currentPath !== path || currentDashboard !== dashboardName) {
        this.$router.replace({
          path,
          query: { dashboard: dashboardName }
        }).catch(() => {})
      }
    },

    loadCustomDashboard() {
      if (this.$attrs?.iframe) {
        this.findDashboard(this.$attrs?.iframe)
      }
    },

    async powerBiDashboardHandler(iframe, iframeId) {
      if (!iframeId) {
        console.warn('iframeId is null, cannot embed powerbi report')
        return
      }

      this.formatedURL = null
      const reportResult = await getCustomDashboardReportURLPowerbi(
        this.$store.getters.getToken,
        iframe.title
      )

      const reportData = reportResult.data
      const iframeUrl = reportData?.reportsDetail?.embedUrl

      const reportLoadConfig = {
        type: 'report',
        tokenType: 1,
        accessToken: reportData.embedToken,
        embedUrl: iframeUrl
      }

      const report = this.powerbi.embed(iframeId, reportLoadConfig)
      report.off('loaded')
      // Triggers when a report schema is successfully loaded
      report.on('loaded', function () {
        console.log('Report load successful')
      })
      // Clear any other rendered handler events
      report.off('rendered')
      // Triggers when a report is successfully embedded in UI
      report.on('rendered', function () {
        console.log('Report render successful')
      })
      // Clear any other error handler events
      report.off('error')
      // Handle embed errors
      report.on('error', function (event) {
        let errorMsg = event.detail
        console.error(errorMsg)
        return
      })

      setTimeout(() => {
        this.$hideLoading()
      }, 5000)
    },

    async lookerStudioDashboardHandler(iframe) {
      try {
        const { data } = await getLookerStudioToken()
        const payload = {
          'ds0.token': data.token,
          'ds0.userId': data.userId
        }
        const params = JSON.stringify(payload)
        const lookerUrlEncoded = iframe.url + `?params=${encodeURI(params)}`
        this.formatedURL = lookerUrlEncoded
        this.renderDremioDashboard = true
      } catch (error) {
        this.$toast(error.response?.data?.message, 'red')
      } finally {
        this.$hideLoading()
      }
    },

    async loadIframe(iframe) {
      this.forceRendering = true
      try {
        this.$showLoading()

        if (!iframe.url) return

        if (this.renderPowerbiDashboard) {
          this.bootstrapPowerBi()
          if (!this.iframeId) return
        }

        if (iframe.url.includes('powerbi')) {
          return this.powerBiDashboardHandler(iframe, this.iframeId)
        }

        if (iframe.url.includes('looker')) {
          return this.lookerStudioDashboardHandler(iframe)
        }

        this.loadDremioDashboard(iframe.url)
      } catch (error) {
        console.log('error', error)
      } finally {
        this.$hideLoading()
      }
    },

    bootstrapPowerBi() {
      this.iframeId = document.getElementById('powerbi-dashboard')
      if (!this.iframeId) {
        console.warn('powerbi-dashboard element not found')
        return
      }

      // clears any previous embed before bootstrap
      try {
        this.powerbi.reset(this.iframeId)
      } catch (error) {
        // ignores error if there's no previous embed
      }

      this.powerbi.bootstrap(this.iframeId, { type: 'report' })
    },

    loadDremioDashboard(url) {
      this.formatedURL = url
      this.renderDremioDashboard = true
      setTimeout(() => {
        this.$hideLoading()
      }, 5000)
    },
    onLoadCallback(iframe = null) {
      const shouldSetupCallback = this.$refs.iframe &&
        iframe?.url?.includes('bi') &&
        !iframe?.url?.includes('powerbi')

      if (!shouldSetupCallback) return

      this.$showLoading()
      this.$refs.iframe.onload = () => {
        const targetOrigin = window.location.origin
        const message = JSON.stringify({
          company: this.$store.getters.getUser.company,
          logo: this.$store.getters.getUser.logo,
          token: this.$store.getters.getToken
        })
        this.$refs.iframe.contentWindow.postMessage(message, targetOrigin)
        this.$hideLoading()
      }
    }
  }
}
</script>

<style lang="stylus" scoped>
.dashboardIFrame
  width: 100%
  height 100vh
#powerbi-dashboard
  height 100%
</style>
